import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import AudioPlayer from '../../../components/AudioPlayer';
import WaveformVisualizer from '../../../components/WaveformVisualizer';
import { getAudioFileUrl } from '../../../services/audioManagementService';

const AudioPlaybackModal = ({ audio, onClose }) => {
  const [signedUrl, setSignedUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadAudioUrl();
  }, [audio]);

  const loadAudioUrl = async () => {
    try {
      setLoading(true);
      setError(null);

      if (audio?.audioUrl) {
        // Extract file path from URL
        const urlParts = audio?.audioUrl?.split('audio-files/');
        if (urlParts?.length > 1) {
          const filePath = urlParts?.[1]?.split('?')?.[0];
          const url = await getAudioFileUrl(filePath);
          setSignedUrl(url);
        } else {
          setSignedUrl(audio?.audioUrl);
        }
      }
    } catch (err) {
      console.error('Error loading audio URL:', err);
      setError(err?.message || 'Failed to load audio');
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const getQualityLabel = (score) => {
    if (score >= 80) return { label: 'Excellent', color: 'text-green-600', bg: 'bg-green-100' };
    if (score >= 60) return { label: 'Good', color: 'text-orange-600', bg: 'bg-orange-100' };
    return { label: 'Needs Work', color: 'text-red-600', bg: 'bg-red-100' };
  };

  const quality = getQualityLabel(audio?.qualityScore);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Music" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Audio Quality Review</h2>
              <p className="text-sm text-muted-foreground">{audio?.lineId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Line Details */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <div className="text-xs text-muted-foreground mb-1">Play</div>
                <div className="text-sm font-medium text-foreground">{audio?.playTitle}</div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground mb-1">Character</div>
                <div className="text-sm font-medium text-foreground">{audio?.character}</div>
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground mb-1">Dialogue</div>
              <div className="text-sm text-foreground italic">"{audio?.dialogue}"</div>
            </div>
          </div>

          {/* Quality Metrics */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Clock" size={16} className="text-primary" />
                <span className="text-xs font-medium text-muted-foreground">Duration</span>
              </div>
              <div className={`text-2xl font-bold ${audio?.durationValid ? 'text-foreground' : 'text-red-600'}`}>
                {formatDuration(audio?.duration)}
              </div>
              {!audio?.durationValid && (
                <div className="text-xs text-red-600 mt-1">Invalid duration</div>
              )}
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Activity" size={16} className="text-primary" />
                <span className="text-xs font-medium text-muted-foreground">Quality Score</span>
              </div>
              <div className={`text-2xl font-bold ${quality?.color}`}>
                {audio?.qualityScore}%
              </div>
              <div className={`text-xs ${quality?.color} mt-1`}>{quality?.label}</div>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="Upload" size={16} className="text-primary" />
                <span className="text-xs font-medium text-muted-foreground">Upload Status</span>
              </div>
              <div className="text-2xl font-bold text-foreground capitalize">
                {audio?.uploadStatus}
              </div>
              <div className={`text-xs mt-1 ${
                audio?.uploadStatus === 'complete' ? 'text-green-600' : 'text-orange-600'
              }`}>
                {audio?.uploadStatus === 'complete' ? 'Ready for review' : 'Pending upload'}
              </div>
            </div>
          </div>

          {/* Audio Player */}
          {loading && (
            <div className="flex items-center justify-center py-8">
              <Icon name="Loader2" size={24} className="animate-spin text-primary" />
              <span className="ml-2 text-muted-foreground">Loading audio...</span>
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 border border-destructive rounded-lg p-4">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={20} className="text-destructive" />
                <p className="text-destructive">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && signedUrl && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Headphones" size={16} className="text-primary" />
                  Audio Playback
                </h3>
                <AudioPlayer 
                  audioUrl={signedUrl}
                  label="Play Recording"
                  size="lg"
                  showDuration={true}
                  onPlayStart={() => {}}
                  onPlayEnd={() => {}}
                />
              </div>

              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Activity" size={16} className="text-primary" />
                  Waveform Visualization
                </h3>
                <WaveformVisualizer 
                  audioUrl={signedUrl}
                  height={120}
                  clarityScore={audio?.qualityScore}
                />
              </div>
            </div>
          )}

          {/* Validation Notes */}
          <div className="bg-muted/50 rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="FileText" size={16} className="text-primary" />
              Quality Checks
            </h3>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Icon 
                  name={audio?.audioUrl ? 'CheckCircle' : 'XCircle'} 
                  size={16} 
                  className={audio?.audioUrl ? 'text-green-600' : 'text-red-600'}
                />
                <span className="text-sm text-foreground">Audio file present</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon 
                  name={audio?.durationValid ? 'CheckCircle' : 'XCircle'} 
                  size={16} 
                  className={audio?.durationValid ? 'text-green-600' : 'text-red-600'}
                />
                <span className="text-sm text-foreground">Duration within acceptable range (0-5 minutes)</span>
              </div>
              <div className="flex items-center gap-2">
                <Icon 
                  name={audio?.qualityScore >= 70 ? 'CheckCircle' : 'XCircle'} 
                  size={16} 
                  className={audio?.qualityScore >= 70 ? 'text-green-600' : 'text-red-600'}
                />
                <span className="text-sm text-foreground">Quality score meets threshold (≥70%)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-border text-foreground hover:bg-muted transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioPlaybackModal;